import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import {
  blankPlanDraft,
  buildPayload,
  draftToNewPlan,
  newProgramToProgram,
  planToDraft,
  programToDraft,
  type BuildReasonCode,
  type Draft,
  type Plan,
  type ProgramStatus,
  type UnitsPreference,
} from "@jackedlogs/shared";
import type { ProgramEditorProps } from "./types";
import { writeError } from "../seams";

// Draft state + save for the program editor / scheduler-composer. Seeds
// from an existing program or a creation NewProgram (no create-vs-edit
// branching in the view — newProgramToProgram synthesises an empty-id
// Program, and the empty id is the POST-vs-PATCH discriminator).
// buildPayload does the matrix→weeks pivot + zod validation; the reason
// is surfaced inline.
//
// SINGLE mode (Plans editor lift, redesign §5.6 / P7): the SAME draft
// model authors a single-workout Plan. Seed via planToDraft / blankPlanDraft,
// save via draftToNewPlan → /api/me/plans. scheduledFor lives here (the
// one Plan field the Draft can't carry); the program path ignores it.
//
// save(statusOverride?) — plain Save persists with the CURRENT status
// (a draft stays a draft: Follow+Publish, "Salva salva sempre"). Publish
// / un-publish pass the target status so content + status land in ONE
// request; the override can't be staged via setDraft-then-save because
// save closes over the pre-setState draft. statusOverride is program-only.
export function useProgramEditor({
  durableWrite,
  program,
  creationSeed,
  onSaved,
  mode = "program",
  plan,
  unitsPreference,
}: Pick<
  ProgramEditorProps,
  "durableWrite" | "program" | "creationSeed" | "onSaved"
> & {
  mode?: "program" | "single";
  plan?: Plan;
  unitsPreference?: UnitsPreference;
}) {
  const { t } = useTranslation();
  const single = mode === "single";
  const isCreate = single ? !plan : !program;
  // The unit the draft's absolute-weight cells are expressed in, FROZEN at
  // seed time. Weights are stored in kg; an lb user reads and types lb, so
  // the seed converts out and the save converts back — and the two have to
  // use the same unit or the round-trip multiplies every prescription by
  // 2.2. Reading the live prop in save() would do exactly that: the profile
  // starts as a synthetic kg DEFAULT and upgrades when the network lands
  // (ProfileContext `loaded`), so a draft seeded mid-load would be saved
  // against a unit it was never written in. A ref pins one answer for the
  // lifetime of the draft; a later preference change is picked up the next
  // time the editor is opened, which is the harmless direction to be wrong.
  const cellUnit = useRef<UnitsPreference>(unitsPreference ?? "kg").current;
  // PE-21 — only decides the editing language of a program that never
  // recorded an author; an existing program's own language wins.
  const lang = useLocale();
  const [draft, setDraft] = useState<Draft>(() =>
    single
      ? plan
        ? planToDraft(plan, cellUnit).draft
        : blankPlanDraft()
      : programToDraft(
          program ?? newProgramToProgram(creationSeed!),
          cellUnit,
          lang,
        ),
  );
  const [scheduledFor, setScheduledFor] = useState<string>(() =>
    single && plan ? planToDraft(plan, cellUnit).scheduledFor : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A failed build/save never shows raw text to the family: the English
  // `reason` / API `err` is a console breadcrumb only, the banner shows a
  // localized message (build → per-code key with errSchema fallback; save
  // → errSave). Shared by both save paths so the two can't drift.
  const showBuildError = useCallback(
    (built: { reason: string; code: BuildReasonCode }) => {
      console.warn("[program editor] build failed:", built.reason);
      setError(
        t(`program.errCode.${built.code}`, {
          defaultValue: t("program.errSchema"),
        }),
      );
    },
    [t],
  );
  const showSaveError = useCallback(
    (err: unknown) => {
      console.warn("[program editor] save failed:", err);
      setError(t("program.errSave"));
    },
    [t],
  );

  // Signature mirrors saveProgram (statusOverride ignored — Plans have no
  // publish/follow lifecycle) so `save` stays one callable type.
  const savePlan = useCallback(async (_statusOverride?: ProgramStatus) => {
    setError(null);
    if (draft.name.trim().length === 0) {
      setError(t("program.errName"));
      return;
    }
    const built = draftToNewPlan(draft, { scheduledFor, cellUnit });
    if (!built.ok) {
      showBuildError(built);
      return;
    }
    setBusy(true);
    try {
      const result = isCreate
        ? await durableWrite({
            method: "POST",
            path: "/api/me/plans",
            body: built.payload,
          })
        : await durableWrite({
            method: "PATCH",
            path: `/api/me/plans/${encodeURIComponent(plan!.id)}`,
            body: built.payload,
          });
      // A queued save closes the editor exactly like a sent one: it is
      // durable, and holding the athlete in a form over a network blip is
      // the behaviour DX-2 removes.
      const err = writeError(result);
      if (err !== null) {
        showSaveError(err);
        return;
      }
      onSaved();
    } finally {
      setBusy(false);
    }
  }, [durableWrite, cellUnit, draft, isCreate, onSaved, plan, scheduledFor, t, showBuildError, showSaveError]);

  const saveProgram = useCallback(async (statusOverride?: ProgramStatus) => {
    setError(null);
    if (draft.name.trim().length === 0) {
      setError(t("program.errName"));
      return;
    }
    // Friendly guard for the schema's slots.min(1): a freshly created
    // program (or a day emptied via the panel's ✕) would otherwise fail
    // with a raw zod path. Same rule, readable words.
    if (draft.sessions.some((s) => s.slots.length === 0)) {
      setError(
        t("program.errEmptyDay", {
          defaultValue: "Every training day needs at least one exercise.",
        }),
      );
      return;
    }
    const built = buildPayload(
      statusOverride ? { ...draft, status: statusOverride } : draft,
      cellUnit,
    );
    if (!built.ok) {
      showBuildError(built);
      return;
    }
    setBusy(true);
    try {
      const result = isCreate
        ? await durableWrite({
            method: "POST",
            path: "/api/me/programs",
            body: built.payload,
          })
        : await durableWrite({
            method: "PATCH",
            path: `/api/me/programs/${encodeURIComponent(program!.id)}`,
            body: built.payload,
          });
      const err = writeError(result);
      if (err !== null) {
        showSaveError(err);
        return;
      }
      // Reflect a publish/un-publish in local state too — hosts that keep
      // the composer mounted after onSaved render the right status line.
      if (statusOverride) {
        setDraft((d) => ({ ...d, status: statusOverride }));
      }
      onSaved();
    } finally {
      setBusy(false);
    }
  }, [durableWrite, cellUnit, draft, isCreate, onSaved, program, t, showBuildError, showSaveError]);

  const save = single ? savePlan : saveProgram;

  return {
    draft,
    setDraft,
    isCreate,
    // Exposed so the composer labels the value column with the unit the
    // cells are ACTUALLY in, never the live preference (they can differ).
    cellUnit,
    busy,
    error,
    save,
    scheduledFor,
    setScheduledFor,
  };
}
